import { useSelector } from 'react-redux';
import key from 'weak-key';
import { useRouter } from 'next/navigation';
import { Row, Col, Divider, Button } from '@geberit/gdds';

// styles
import styles from './investors-calendar.module.scss';

// types
import type { InvestorsCalendarRowProps } from './investors-calendar.types';

// components
import { Translation } from 'components/Translation/Translation';
import Icalendar from './icalendar';

// utils
import { isEmpty } from 'utils/is-empty';
import { validateDate } from 'utils/validateDate';
import { decodingContent } from 'utils/decodingContent';
import { useIsDesktop } from 'components/App/SizeProvider';
import { groupDataSelector } from 'utils/selectors/globalsSelectors';

function InvestorsCalendarRow({
  title,
  startDate,
  agenda,
  pastEvent,
  linkTarget,
  linkText,
  locale,
  downloadIds,
  language,
  ...props
}: Readonly<InvestorsCalendarRowProps>) {
  const isDesktop = useIsDesktop();
  const router = useRouter();
  const sortedAgenda = !isEmpty(agenda) && agenda.sort((a, b) => a.time - b.time);

  const groupData = useSelector(groupDataSelector);
  const dlcUrl = `${groupData?.downloadcenter?.url}?ids=${downloadIds?.join(
    ',',
  )}&title=${encodeURIComponent(title)}`;
  const pastEventUrl = pastEvent && !isEmpty(downloadIds) ? dlcUrl : linkTarget;
  const uppercaseLang = language?.substring(0, 2);
  const dateformat = uppercaseLang === 'DE' ? 'dd. MMMM yyyy' : 'dd MMMM yyyy';

  return (
    <>
      <div className={styles.rowContent}>
        <Row>
          <Col size={[4, 8, 6]}>
            <div className={styles.titleWrapper}>
              <div>
                <span className={`${styles.bold} ${styles.title}`}>{decodingContent(title)}</span>
                {!isDesktop && (
                  <span className={`${styles.bold} ${styles.date}`}>
                    {validateDate(new Date(startDate), dateformat, locale)}
                  </span>
                )}
              </div>
              {!isDesktop && !pastEvent && (
                <div>
                  <Icalendar title={title} startDate={startDate} onlyIcon {...props} />
                </div>
              )}

              {!isDesktop && pastEvent && pastEventUrl && (
                <Button
                  onClick={() => {
                    window.sessionStorage.setItem('filter1', startDate.substring(0, 4));
                    router.push(pastEventUrl);
                  }}
                  iconAlignment="right"
                  symbol="ExternalLink"
                  stylingType="flat"
                  isIcon
                />
              )}
            </div>
          </Col>
          {isDesktop && (
            <Col size={[4, 8, 3]}>
              <span className={`${styles.bold} ${styles.date}`}>
                {validateDate(new Date(startDate), dateformat, locale)}
              </span>
            </Col>
          )}
          {isDesktop && (
            <Col size={[4, 8, 3]}>
              {pastEvent && pastEventUrl && (
                <div className={styles.bold}>
                  <Button
                    onClick={() => {
                      const yearFilter = startDate.substring(0, 4);
                      if (yearFilter) {
                        window.sessionStorage.setItem('filter1', yearFilter);
                      }
                      router.push(pastEventUrl);
                    }}
                    iconAlignment="right"
                    symbol="ExternalLink"
                    stylingType="flat"
                  >
                    {linkText}
                  </Button>
                </div>
              )}
              {!pastEvent && <Icalendar title={title} startDate={startDate} {...props} />}
            </Col>
          )}
        </Row>
        {!isEmpty(sortedAgenda) &&
          sortedAgenda.map((entry) => (
            <Row className={styles.agenda} key={key({ entry })}>
              <Col size={[4, 8, 6]}>
                <span>{decodingContent(entry.title)}</span>
              </Col>
              {entry.time && (
                <Col size={[4, 8, 3]}>
                  <span>{entry.time} </span>
                  <Translation id="group_investorcalendar_timezone" />
                </Col>
              )}
            </Row>
          ))}
      </div>
      <Divider />
    </>
  );
}

export default InvestorsCalendarRow;
